import { useMemo, useState } from "react";
import { Calendar as CalendarIcon, X, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

// Date-field key -> label & task property it maps to.
export const DATE_FIELDS = [
  { key: "created_at", label: "Created date" },
  { key: "due_date", label: "Due date" },
  { key: "done_at", label: "Completed date" },
];

/**
 * Filters an array of task objects by a chosen date field and range.
 * - `field` is one of created_at / due_date / done_at.
 * - `from` / `to` are ISO date strings (yyyy-mm-dd) or empty.
 * - Tasks missing the chosen date are excluded whenever any range is set.
 */
export function filterTasksByDate(tasks, { field, from, to }) {
  if (!from && !to) return tasks;
  const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
  const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
  return tasks.filter((t) => {
    const raw = t?.[field];
    if (!raw) return false;
    const ms = new Date(raw).getTime();
    if (Number.isNaN(ms)) return false;
    if (fromMs !== null && ms < fromMs) return false;
    if (toMs !== null && ms > toMs) return false;
    return true;
  });
}

/**
 * Compact date-range filter used at the top of both owner & employee
 * Task views. Renders a popover trigger showing the current selection
 * so it doesn't take up space on small screens.
 */
export default function TaskDateFilter({ value, onChange, testId = "task-date-filter" }) {
  const { field, from, to } = value;
  const [open, setOpen] = useState(false);

  const active = !!(from || to);
  const fieldLabel = useMemo(
    () => DATE_FIELDS.find((f) => f.key === field)?.label || "Date",
    [field]
  );

  const summary = active
    ? `${fieldLabel}: ${from || "…"} → ${to || "…"}`
    : "Filter by date";

  const clear = () => {
    onChange({ field, from: "", to: "" });
  };

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`gap-1.5 ${active ? "border-[#0055FF] text-[#0055FF]" : ""}`}
            data-testid={`${testId}-trigger`}
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            <span className="mono text-[11px] max-w-[200px] truncate">{summary}</span>
            {active && <Filter className="w-3 h-3" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-3 space-y-3">
          <div className="space-y-1.5">
            <label className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Filter by
            </label>
            <Select
              value={field}
              onValueChange={(v) => onChange({ ...value, field: v })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid={`${testId}-field`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FIELDS.map((f) => (
                  <SelectItem key={f.key} value={f.key}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <label className="mono text-[10px] uppercase tracking-widest text-slate-500">
                From
              </label>
              <Input
                type="date"
                value={from}
                onChange={(e) => onChange({ ...value, from: e.target.value })}
                className="h-8 text-xs"
                max={to || undefined}
                data-testid={`${testId}-from`}
              />
            </div>
            <div className="space-y-1.5">
              <label className="mono text-[10px] uppercase tracking-widest text-slate-500">
                To
              </label>
              <Input
                type="date"
                value={to}
                onChange={(e) => onChange({ ...value, to: e.target.value })}
                className="h-8 text-xs"
                min={from || undefined}
                data-testid={`${testId}-to`}
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={clear}
              disabled={!active}
              className="mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-red-600 disabled:opacity-40 disabled:hover:text-slate-500 flex items-center gap-1"
              data-testid={`${testId}-clear`}
            >
              <X className="w-3 h-3" /> Clear
            </button>
            <Button
              size="sm"
              className="h-7 bg-[#0055FF] hover:bg-[#0040CC] text-xs"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-apply`}
            >
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {active && (
        <button
          type="button"
          onClick={clear}
          className="text-slate-400 hover:text-red-600"
          title="Clear filter"
          data-testid={`${testId}-clear-quick`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
